// dwaar/src/components/OraPanel.jsx
// Repo: terralekha-core | Folder: dwaar/src/components/
// Ora — TERRALEKHA AI compliance assistant panel.
//   Collapsible chat drawer embedded in the Samiksha PassPort detail page.
//
// Props:
//   passportId  (string)  — UUID of the current PassPort
//   accessToken (string)  — Supabase session JWT for Bearer auth
//   cnCode      (string)  — 8-digit CN code (shown in context line)
//   status      (string)  — passport status ('draft' | 'pending_confirmation' | ...)
//
// Behaviour:
//   - Toggle button ("Ask Ora") is always visible in the PassPort page.
//   - Panel hidden by default; opens on toggle click.
//   - Static greeting shown on open — no Anthropic call at open time.
//   - Conversation history held in React state (in-memory for this session).
//   - Each user message is appended to history before POST to /api/ora/chat.
//   - Assistant reply is appended to history on success.
//   - On error: generic "Unable to reach Ora. Please try again." shown.
//   - Send disabled while loading or when input is blank.

import React, { useRef, useEffect, useState } from "react";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const STATIC_GREETING =
  "Hello, I'm Ora — your TERRALEKHA compliance advisor. " +
  "Ask me anything about your CBAM obligations, GHG accounting, " +
  "or what your emission figures mean for your EU exports.";

const ORA_ERROR_MSG = "Unable to reach Ora. Please try again.";

// ── OraPanel ──────────────────────────────────────────────────────────────────

export default function OraPanel({ passportId, accessToken, cnCode, status }) {
  const [isOpen,   setIsOpen]   = useState(false);
  const [messages, setMessages] = useState([]);   // { role, content }[]
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (isOpen && bottomRef.current && typeof bottomRef.current.scrollIntoView === "function") {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  function handleToggle() {
    setIsOpen((prev) => !prev);
    setError(null);
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;

    const userMessage    = { role: "user", content: text };
    const nextHistory    = [...messages, userMessage];

    setMessages(nextHistory);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/ora/chat`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          passport_id: passportId,
          messages:    nextHistory,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(ORA_ERROR_MSG);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.reply },
        ]);
      }
    } catch {
      setError(ORA_ERROR_MSG);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="tl-ora-wrapper">
      {/* Toggle button — always visible */}
      <button
        className="tl-ora-toggle"
        onClick={handleToggle}
        aria-label={isOpen ? "Close Ora" : "Ask Ora"}
        aria-expanded={isOpen}
      >
        {isOpen ? "✕ Close Ora" : "✦ Ask Ora"}
      </button>

      {/* Panel */}
      {isOpen && (
        <section
          className="tl-ora-panel"
          role="region"
          aria-label="Ora"
        >
          {/* Panel header */}
          <div className="tl-ora-header">
            <span className="tl-ora-title">Ora</span>
            <span className="tl-ora-context">
              CN {cnCode} · {status?.replace(/_/g, " ").toUpperCase()}
            </span>
          </div>

          {/* Message list */}
          <div className="tl-ora-messages" role="log" aria-live="polite">
            {/* Static greeting */}
            <div className="tl-ora-msg tl-ora-msg--assistant">
              <p className="tl-ora-bubble">{STATIC_GREETING}</p>
            </div>

            {/* Conversation history */}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`tl-ora-msg tl-ora-msg--${msg.role}`}
              >
                <p className="tl-ora-bubble">{msg.content}</p>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="tl-ora-msg tl-ora-msg--assistant">
                <p className="tl-ora-bubble tl-ora-bubble--loading">
                  Ora is thinking…
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="tl-ora-msg tl-ora-msg--error" role="alert">
                <p className="tl-ora-bubble tl-ora-bubble--error">{error}</p>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input row */}
          <div className="tl-ora-input-row">
            <label htmlFor="ora-input" className="tl-visually-hidden">
              Your question
            </label>
            <textarea
              id="ora-input"
              ref={inputRef}
              className="tl-ora-input"
              aria-label="Your question"
              placeholder="Ask about your emissions, CBAM obligations, or next steps…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              rows={2}
            />
            <button
              className="tl-ora-send"
              aria-label="Send"
              onClick={handleSend}
              disabled={loading || !input.trim()}
            >
              {loading ? "…" : "Send"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
